import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, MapPin, CalendarDays, Loader2, ArrowLeft, Star, Users,
  Sparkles, Globe, Building2, ExternalLink, ArrowUpRight, Video,
  ShieldCheck, Lock, Image as ImageIcon, X, Share2, Copy, Linkedin, MessageCircle,
} from "lucide-react";
import Seo from "../components/Seo";
import AppLayout from "../components/AppLayout";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import { EVENT_TYPES, EVENT_FORMATS, EVENT_CATEGORIES, INDUSTRIES } from "../data/hub";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL = "__all__";

function DemoBadge() {
  return (
    <span className="rounded-full bg-[color:var(--brand-teal)]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--brand-teal)]">
      Demo event
    </span>
  );
}

function StatusPill({ status }) {
  const map = {
    approved: { bg: "var(--brand-green)", label: "Approved" },
    pending: { bg: "var(--brand-orange)", label: "Pending review" },
    rejected: { bg: "var(--brand-red)", label: "Rejected" },
    draft: { bg: "var(--ink-soft)", label: "Draft" },
    archived: { bg: "var(--ink-soft)", label: "Archived" },
  };
  const c = map[status] || map.draft;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em]"
      style={{ backgroundColor: `color-mix(in srgb, ${c.bg} 12%, transparent)`, color: c.bg }}
    >
      {c.label}
    </span>
  );
}

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" }) +
      " · " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function FormatIcon({ format, className = "h-3.5 w-3.5" }) {
  if (format === "Online") return <Video className={className} />;
  if (format === "Hybrid") return <Sparkles className={className} />;
  return <MapPin className={className} />;
}

function ShareEventMenu({ event, testId = "event-share-btn" }) {
  const url = typeof window !== "undefined"
    ? `${window.location.origin}/app/events/${event.id}`
    : `/app/events/${event.id}`;
  const text = `${event.title} — on StUpEco Hub`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied.");
    } catch { toast.error("Could not copy link."); }
  };
  const openLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, "_blank", "noopener,noreferrer");
  };
  const openWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={testId}
          className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--hairline)] bg-[color:var(--surface)] px-4 py-2.5 text-sm font-semibold text-[color:var(--ink)] transition-colors hover:border-[color:var(--ink)]"
        >
          <Share2 className="h-4 w-4" /> Share
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" data-testid="event-share-menu" className="min-w-[200px]">
        <DropdownMenuItem onSelect={copy} data-testid="event-share-copy">
          <Copy className="mr-2 h-4 w-4" /> Copy link
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={openLinkedIn} data-testid="event-share-linkedin">
          <Linkedin className="mr-2 h-4 w-4" /> Share on LinkedIn
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={openWhatsApp} data-testid="event-share-whatsapp">
          <MessageCircle className="mr-2 h-4 w-4" /> Share on WhatsApp
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function OrganizerCta({ canCreate, tier }) {
  return (
    <section
      className="mt-10 overflow-hidden rounded-3xl border border-[color:var(--hairline)] bg-[color:var(--surface)]"
      data-testid="events-organizer-cta"
    >
      <div className="brand-bar h-1 w-full" />
      <div className="grid grid-cols-1 gap-6 p-8 md:grid-cols-[1.5fr_1fr] md:items-center">
        <div>
          <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-[color:var(--brand-orange)]">
            <Sparkles className="h-3.5 w-3.5" /> For Organizers
          </p>
          <h2 className="font-display mt-2 text-2xl font-semibold text-[color:var(--ink)] sm:text-3xl">
            Organizing a professional event?
          </h2>
          <p className="mt-2 max-w-xl text-sm text-[color:var(--ink-soft)]">
            Reach founders, businesses, professionals and investors across the StUpEco Hub network.
            Every event is reviewed by our team before it goes live.
          </p>
        </div>
        <div className="flex flex-col items-start gap-3 md:items-end">
          <Link
            to="/app/events/create"
            data-testid="events-organizer-list-cta"
            className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
          >
            {canCreate ? <Sparkles className="h-4 w-4" /> : <Lock className="h-4 w-4" />} List Your Event
          </Link>
          <p className="text-xs text-[color:var(--ink-soft)]">
            {canCreate ? "Available on your plan" : `You're on ${tier} — Standard or Premium unlocks event listings.`}
          </p>
        </div>
      </div>
    </section>
  );
}

function EventCard({ e, featured = false }) {
  return (
    <Link
      to={`/app/events/${e.id}`}
      data-testid={`event-card-${e.id}`}
      className={`hub-card group flex flex-col overflow-hidden rounded-2xl transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_40px_rgba(10,10,10,0.06)] ${featured ? "ring-1 ring-[color:var(--brand-teal)]/40" : ""}`}
    >
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-[color:var(--soft)]">
        {e.image ? (
          <img src={e.image} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <CalendarDays className="h-8 w-8 text-[color:var(--ink-soft)]/40" />
          </div>
        )}
        <div className="absolute left-3 top-3 flex items-center gap-1.5">
          {featured && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[color:var(--invert-bg)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white">
              <Star className="h-3 w-3" /> Featured
            </span>
          )}
          {e.is_demo && <DemoBadge />}
        </div>
        <div className="absolute right-3 top-3">
          <span className="inline-flex items-center gap-1 rounded-full bg-[color:var(--surface-glass)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--ink)] backdrop-blur-md">
            <FormatIcon format={e.format} /> {e.format}
          </span>
        </div>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--brand-teal)]">{e.event_type}</p>
        <h3 className="font-display mt-1 text-base font-semibold text-[color:var(--ink)] line-clamp-2">{e.title}</h3>
        <p className="mt-1 flex items-center gap-1 text-xs text-[color:var(--ink-soft)]">
          <CalendarDays className="h-3 w-3" /> {formatWhen(e.start_datetime)}
        </p>
        {(e.city || e.country) && (
          <p className="mt-1 flex items-center gap-1 text-xs text-[color:var(--ink-soft)]">
            <MapPin className="h-3 w-3" /> {[e.city, e.country].filter(Boolean).join(", ")}
          </p>
        )}
        <p className="mt-3 text-sm text-[color:var(--ink-soft)] line-clamp-2">{e.description}</p>
        <div className="mt-4 flex items-center justify-between border-t border-[color:var(--hairline)] pt-3 text-xs text-[color:var(--ink-soft)]">
          <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" />{e.organizer_name || "Community"}</span>
          <ArrowUpRight className="h-4 w-4 text-[color:var(--ink)] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </div>
      </div>
    </Link>
  );
}

export function EventsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [eventType, setEventType] = useState("");
  const [format, setFormat] = useState("");
  const [country, setCountry] = useState("");
  const [when, setWhen] = useState("upcoming");
  const { user } = useAuth();

  const load = async () => {
    setLoading(true);
    try {
      const params = { when };
      if (q) params.q = q;
      if (eventType) params.event_type = eventType;
      if (format) params.format = format;
      if (country) params.country = country;
      const { data } = await axios.get(`${API}/hub/events`, { params, withCredentials: true });
      setItems(data.items || []);
    } catch { toast.error("Failed to load events."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventType, format, country, when]);

  const distinctCountries = useMemo(
    () => [...new Set(items.map((i) => i.country).filter(Boolean))].sort(),
    [items],
  );
  const featured = items.filter((e) => e.featured);
  const rest = items.filter((e) => !e.featured);

  const tier = user?.membership_tier || "free";
  const canCreate = tier === "standard" || tier === "premium";

  return (
    <AppLayout>
      <Seo title="Events" description="Discover professional events across the StUpEco Hub network." path="/app/events" />
      <div className="container-hub py-8 lg:py-10" data-testid="events-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[color:var(--brand-teal)]">Events</p>
            <h1 className="font-display mt-2 text-3xl font-semibold tracking-[-0.02em] text-[color:var(--ink)] sm:text-4xl">
              Discover what's on across the Hub.
            </h1>
            <p className="mt-2 text-sm text-[color:var(--ink-soft)]">
              Seminars, workshops, expos, meetups and investor evenings — from members like you.
            </p>
          </div>
          <Link
            to="/app/events/create"
            data-testid="events-create-cta"
            className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
          >
            <Sparkles className="h-4 w-4" /> Submit an event
            {!canCreate && <Lock className="ml-1 h-3.5 w-3.5" />}
          </Link>
        </div>

        {/* Filters */}
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="mt-8 flex flex-wrap items-end gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--ink-soft)]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search events, organizers, categories"
              className="h-11 rounded-xl pl-9"
              data-testid="events-search"
            />
          </div>
          <Select value={eventType || ALL} onValueChange={(v) => setEventType(v === ALL ? "" : v)}>
            <SelectTrigger data-testid="events-filter-type" className="h-11 w-[170px] rounded-xl"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All types</SelectItem>{EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={format || ALL} onValueChange={(v) => setFormat(v === ALL ? "" : v)}>
            <SelectTrigger data-testid="events-filter-format" className="h-11 w-[150px] rounded-xl"><SelectValue placeholder="Format" /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All formats</SelectItem>{EVENT_FORMATS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={country || ALL} onValueChange={(v) => setCountry(v === ALL ? "" : v)}>
            <SelectTrigger data-testid="events-filter-country" className="h-11 w-[170px] rounded-xl"><SelectValue placeholder="Country" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All countries</SelectItem>
              {distinctCountries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="inline-flex rounded-full border border-[color:var(--hairline)] bg-[color:var(--surface)] p-1" data-testid="events-when-toggle">
            {["upcoming", "past"].map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setWhen(k)}
                data-testid={`events-when-${k}`}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize transition-colors ${when === k ? "bg-[color:var(--invert-bg)] text-white" : "text-[color:var(--ink-soft)]"}`}
              >
                {k}
              </button>
            ))}
          </div>
        </form>

        {loading ? (
          <div className="mt-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-[color:var(--ink-soft)]" /></div>
        ) : items.length === 0 ? (
          <div className="mt-12 rounded-2xl border border-dashed border-[color:var(--hairline)] bg-[color:var(--surface)] p-10 text-center" data-testid="events-empty">
            <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-[color:var(--soft)] text-[color:var(--ink-soft)]">
              <CalendarDays className="h-5 w-5" />
            </span>
            <h3 className="font-display mt-4 text-lg font-semibold text-[color:var(--ink)]">No events match your filters.</h3>
            <p className="mt-2 text-sm text-[color:var(--ink-soft)]">
              Try widening the filters, or {canCreate ? "publish one yourself" : "upgrade to Standard to publish your own event"}.
            </p>
          </div>
        ) : (
          <>
            {featured.length > 0 && (
              <section className="mt-8" data-testid="events-featured">
                <h2 className="font-display text-lg font-semibold text-[color:var(--ink)]">Featured</h2>
                <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
                  {featured.map((e) => <EventCard key={e.id} e={e} featured />)}
                </div>
              </section>
            )}
            <section className="mt-10" data-testid="events-list">
              <h2 className="font-display text-lg font-semibold text-[color:var(--ink)]">
                {when === "upcoming" ? "Upcoming events" : "Past events"}
              </h2>
              <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {rest.map((e) => <EventCard key={e.id} e={e} />)}
              </div>
            </section>
          </>
        )}

        <OrganizerCta canCreate={canCreate} tier={tier} />
      </div>
    </AppLayout>
  );
}

export function EventDetailPage() {
  const { id } = useParams();
  const [e, setE] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/hub/events/${id}`, { withCredentials: true });
      setE(data);
    } catch (err) {
      toast.error(err.response?.status === 404 ? "Event not found." : "Failed to load event.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const toggleInterest = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/hub/events/${id}/interest`, {}, { withCredentials: true });
      setE((prev) => prev && { ...prev, i_am_interested: data.interested,
        interest_count: (prev.interest_count || 0) + (data.interested ? 1 : -1) });
      toast.success(data.interested ? "You're marked interested." : "Removed from your interested list.");
    } catch { toast.error("Could not update. Please try again."); }
    finally { setSaving(false); }
  };

  if (loading) return <AppLayout><div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[color:var(--ink-soft)]" /></div></AppLayout>;
  if (!e) return <AppLayout><div className="container-hub py-10">Event not found. <Link to="/app/events" className="underline">Back to events</Link>.</div></AppLayout>;

  return (
    <AppLayout>
      <Seo title={e.title} description={e.description?.slice(0, 160)} path={`/app/events/${e.id}`} image={e.image || undefined} />
      <div className="container-hub py-8 lg:py-10" data-testid="event-detail-page">
        <Link to="/app/events" className="mb-3 inline-flex items-center gap-1 text-xs text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]">
          <ArrowLeft className="h-3.5 w-3.5" /> Events
        </Link>

        <div className="hub-card overflow-hidden rounded-3xl">
          <div className="relative aspect-[16/6] w-full overflow-hidden bg-[color:var(--soft)]">
            {e.image ? (
              <img src={e.image} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="brand-bar h-full w-full opacity-30" />
            )}
            <div className="absolute left-4 top-4 flex items-center gap-1.5">
              {e.featured && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[color:var(--invert-bg)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white">
                  <Star className="h-3 w-3" /> Featured
                </span>
              )}
              {e.is_demo && <DemoBadge />}
              {e.status !== "approved" && <StatusPill status={e.status} />}
            </div>
          </div>

          <div className="p-8 lg:p-10">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--brand-teal)]">
                  {e.event_type}{e.categories?.[0] ? ` · ${e.categories[0]}` : ""}
                </p>
                <h1 className="font-display mt-2 text-3xl font-semibold tracking-[-0.02em] text-[color:var(--ink)] sm:text-4xl">
                  {e.title}
                </h1>
                <p className="mt-3 inline-flex items-center gap-2 text-sm text-[color:var(--ink-soft)]">
                  <Building2 className="h-4 w-4" /> Organized by <span className="font-semibold text-[color:var(--ink)]">{e.organizer_name || "Community"}</span>
                </p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="flex items-center gap-2">
                  <ShareEventMenu event={e} />
                  <button
                    type="button"
                    onClick={toggleInterest}
                    disabled={saving || e.status !== "approved"}
                    data-testid="event-interest-btn"
                    className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-transform hover:-translate-y-0.5 disabled:opacity-60 ${
                      e.i_am_interested
                        ? "border border-[color:var(--brand-green)] bg-[color:var(--brand-green)]/10 text-[color:var(--brand-green)]"
                        : "bg-[color:var(--invert-bg)] text-white"
                    }`}
                  >
                    <Star className={`h-4 w-4 ${e.i_am_interested ? "fill-[color:var(--brand-green)]" : ""}`} />
                    {e.i_am_interested ? "I'm interested" : "Mark I'm interested"}
                  </button>
                </div>
                <p className="text-xs text-[color:var(--ink-soft)]" data-testid="event-interest-count">
                  <Users className="mr-1 inline h-3 w-3" /> {e.interest_count || 0} interested
                </p>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <MetaBlock icon={CalendarDays} label="When" value={formatWhen(e.start_datetime)} sub={e.end_datetime ? `Ends ${formatWhen(e.end_datetime)}` : ""} />
              <MetaBlock icon={FormatIconInline({ format: e.format })} label="Format" value={e.format} sub={e.timezone_str || ""} />
              <MetaBlock icon={MapPin} label="Location" value={[e.venue, e.city, e.country].filter(Boolean).join(", ") || (e.format === "Online" ? "Online" : "—")} />
              <MetaBlock icon={Globe} label="Registration" value={e.registration_url ? "External link" : "No link"} link={e.registration_url} />
            </div>

            {e.description && (
              <section className="mt-8">
                <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--ink-soft)]">About this event</p>
                <p className="mt-2 whitespace-pre-line text-[color:var(--ink)]">{e.description}</p>
              </section>
            )}

            {(e.categories?.length || e.industries?.length) > 0 && (
              <section className="mt-6 flex flex-wrap gap-2">
                {(e.categories || []).map((c) => (
                  <span key={c} className="rounded-full bg-[color:var(--soft)] px-2.5 py-1 text-xs text-[color:var(--ink)]">{c}</span>
                ))}
                {(e.industries || []).map((c) => (
                  <span key={`ind-${c}`} className="rounded-full border border-[color:var(--hairline)] px-2.5 py-1 text-xs text-[color:var(--ink-soft)]">{c}</span>
                ))}
              </section>
            )}

            {e.registration_url && (
              <div className="mt-8 rounded-2xl border border-[color:var(--hairline)] bg-[color:var(--surface)] p-5">
                <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--ink-soft)]">Registration</p>
                <p className="mt-2 text-sm text-[color:var(--ink-soft)]">Registration is handled by the organiser on an external page.</p>
                <a
                  href={e.registration_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="event-external-register"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-[color:var(--invert-bg)] px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
                >
                  Open registration <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function FormatIconInline({ format }) {
  if (format === "Online") return Video;
  if (format === "Hybrid") return Sparkles;
  return MapPin;
}

function MetaBlock({ icon: Icon, label, value, sub, link }) {
  const content = (
    <>
      <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[color:var(--ink-soft)]">
        <Icon className="h-3.5 w-3.5" /> {label}
      </span>
      <p className="mt-1 text-sm font-semibold text-[color:var(--ink)]">{value || "—"}</p>
      {sub && <p className="text-[11px] text-[color:var(--ink-soft)]">{sub}</p>}
    </>
  );
  if (link) {
    return (
      <a href={link} target="_blank" rel="noreferrer" className="block rounded-xl border border-[color:var(--hairline)] bg-[color:var(--surface)] p-4 transition-colors hover:border-[color:var(--ink)]">
        {content}
      </a>
    );
  }
  return <div className="rounded-xl border border-[color:var(--hairline)] bg-[color:var(--surface)] p-4">{content}</div>;
}

// ---- Event creation ----

const CHIP_CLS = (active) =>
  `cursor-pointer rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
    active ? "border-[color:var(--ink)] bg-[color:var(--ink)] text-white" : "border-[color:var(--hairline)] text-[color:var(--ink-soft)] hover:border-[color:var(--ink)]"
  }`;

function fileToDataUrl(file, maxSide = 1280) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) return reject(new Error("Please upload an image."));
    if (file.size > 3 * 1024 * 1024) return reject(new Error("Image must be under 3MB."));
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = () => reject(new Error("Invalid image."));
      img.src = reader.result;
    };
    reader.onerror = () => reject(new Error("Could not read file."));
    reader.readAsDataURL(file);
  });
}

export function CreateEventPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const tier = user?.membership_tier || "free";
  const canCreate = tier === "standard" || tier === "premium";

  const [f, setF] = useState({
    title: "", event_type: "Conference", description: "",
    start_datetime: "", end_datetime: "", timezone_str: "",
    format: "Physical", venue: "", city: "", country: "",
    event_url: "", registration_url: "",
    professional_declaration: false,
    image: null,
  });
  const [cats, setCats] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const uploadImage = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    try {
      const url = await fileToDataUrl(file);
      set("image", url);
    } catch (e) { toast.error(e.message || "Could not use that image."); }
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!canCreate) return;
    if (!f.title || f.title.length < 3) return toast.error("Please add an event title.");
    if (!f.description || f.description.length < 10) return toast.error("Please add a description.");
    if (!f.start_datetime) return toast.error("Please pick a start date & time.");
    if (!f.professional_declaration) return toast.error("Please confirm this is a professional event.");
    setSaving(true);
    try {
      const payload = {
        ...f,
        start_datetime: new Date(f.start_datetime).toISOString(),
        end_datetime: f.end_datetime ? new Date(f.end_datetime).toISOString() : null,
        categories: cats,
        industries,
      };
      const { data } = await axios.post(`${API}/hub/events`, payload, { withCredentials: true });
      toast.success("Submitted for review. We'll notify you once approved.");
      navigate(`/app/events/${data.event.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit. Please review the form.");
    } finally { setSaving(false); }
  };

  if (!canCreate) {
    return (
      <AppLayout>
        <Seo title="Publish an event" path="/app/events/create" />
        <div className="container-hub py-10" data-testid="events-upgrade-gate">
          <Link to="/app/events" className="mb-3 inline-flex items-center gap-1 text-xs text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]">
            <ArrowLeft className="h-3.5 w-3.5" /> Events
          </Link>
          <div className="hub-card overflow-hidden rounded-3xl">
            <div className="brand-bar h-1 w-full" />
            <div className="grid grid-cols-1 gap-8 p-8 md:grid-cols-[1.4fr_1fr]">
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--soft)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--ink-soft)]">
                  <Lock className="h-3 w-3" /> Standard & Premium
                </span>
                <h1 className="font-display mt-4 text-3xl font-semibold text-[color:var(--ink)] sm:text-4xl">
                  Publishing events is a Standard perk.
                </h1>
                <p className="mt-3 max-w-xl text-[color:var(--ink-soft)]">
                  StUpEco Hub Standard members can submit professional events — seminars, expos,
                  workshops, panels and investor evenings — to the entire network. Every event is
                  moderated by our team to keep the quality bar high.
                </p>
                <ul className="mt-6 space-y-2 text-sm text-[color:var(--ink)]">
                  {[
                    "Reach discoverable members across countries and industries",
                    "Featured slots for Premium members' flagship events",
                    "Track how many members marked themselves interested",
                    "Publish alongside your business profile and offers",
                  ].map((it) => (
                    <li key={it} className="flex items-start gap-2">
                      <ShieldCheck className="mt-0.5 h-4 w-4 text-[color:var(--brand-green)]" /> {it}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/membership"
                    data-testid="events-upgrade-cta"
                    className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
                  >
                    See membership tiers<ArrowUpRight className="h-4 w-4" />
                  </Link>
                  <Link
                    to="/app/events"
                    className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--hairline)] bg-[color:var(--surface)] px-5 py-2.5 text-sm font-semibold text-[color:var(--ink)] hover:border-[color:var(--ink)]"
                  >
                    Explore events
                  </Link>
                </div>
              </div>
              <div className="rounded-2xl bg-[color:var(--soft)] p-6">
                <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--ink-soft)]">You're currently on</p>
                <p className="font-display mt-2 text-2xl font-semibold capitalize text-[color:var(--ink)]">{tier} member</p>
                <div className="mt-4 border-t border-[color:var(--hairline)] pt-4 text-sm text-[color:var(--ink-soft)]">
                  <p>Payments and self-serve upgrades launch soon. For now, contact the Hub team to
                  activate Standard.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Seo title="Publish an event" path="/app/events/create" />
      <div className="container-hub py-8 lg:py-10" data-testid="event-create-page">
        <Link to="/app/events" className="mb-3 inline-flex items-center gap-1 text-xs text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]">
          <ArrowLeft className="h-3.5 w-3.5" /> Events
        </Link>
        <form onSubmit={submit} className="hub-card rounded-3xl p-8 lg:p-10">
          <div className="brand-bar mb-6 h-1 w-16 rounded-full" />
          <h1 className="font-display text-2xl font-semibold text-[color:var(--ink)] sm:text-3xl">Publish a new event</h1>
          <p className="mt-2 max-w-2xl text-sm text-[color:var(--ink-soft)]">
            Every event is reviewed by our team before it goes live in the directory.
          </p>

          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Field label="Event title" required>
              <Input data-testid="event-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Gulf Founders Summit 2026" className="h-11 rounded-xl" maxLength={200} />
            </Field>
            <Field label="Event type" required>
              <Select value={f.event_type} onValueChange={(v) => set("event_type", v)}>
                <SelectTrigger data-testid="event-type" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>

            <Field label="Start date & time" required>
              <Input data-testid="event-start" type="datetime-local" value={f.start_datetime} onChange={(e) => set("start_datetime", e.target.value)} className="h-11 rounded-xl" />
            </Field>
            <Field label="End date & time" hint="Optional">
              <Input data-testid="event-end" type="datetime-local" value={f.end_datetime} onChange={(e) => set("end_datetime", e.target.value)} className="h-11 rounded-xl" />
            </Field>

            <Field label="Format" required>
              <Select value={f.format} onValueChange={(v) => set("format", v)}>
                <SelectTrigger data-testid="event-format" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{EVENT_FORMATS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Timezone" hint="Optional (e.g. Asia/Dubai)">
              <Input data-testid="event-tz" value={f.timezone_str} onChange={(e) => set("timezone_str", e.target.value)} placeholder="Asia/Dubai" className="h-11 rounded-xl" />
            </Field>

            {f.format !== "Online" && (
              <>
                <Field label="Venue" hint="Optional">
                  <Input data-testid="event-venue" value={f.venue} onChange={(e) => set("venue", e.target.value)} placeholder="Venue name" className="h-11 rounded-xl" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="City">
                    <Input data-testid="event-city" value={f.city} onChange={(e) => set("city", e.target.value)} className="h-11 rounded-xl" />
                  </Field>
                  <Field label="Country">
                    <Input data-testid="event-country" value={f.country} onChange={(e) => set("country", e.target.value)} className="h-11 rounded-xl" />
                  </Field>
                </div>
              </>
            )}

            <Field label="Event page URL" hint="Optional">
              <Input data-testid="event-url" value={f.event_url} onChange={(e) => set("event_url", e.target.value)} placeholder="https://" className="h-11 rounded-xl" />
            </Field>
            <Field label="Registration URL" hint="Optional external registration link">
              <Input data-testid="event-reg-url" value={f.registration_url} onChange={(e) => set("registration_url", e.target.value)} placeholder="https://" className="h-11 rounded-xl" />
            </Field>
          </div>

          <div className="mt-6">
            <Field label="Description" required hint="What is the event about? Who is it for?">
              <Textarea
                data-testid="event-description"
                value={f.description}
                onChange={(e) => set("description", e.target.value)}
                className="min-h-40 rounded-xl"
                maxLength={4000}
                placeholder="Explain the event, target audience, speakers, agenda…"
              />
            </Field>
          </div>

          <div className="mt-6">
            <Label>Categories</Label>
            <p className="text-xs text-[color:var(--ink-soft)]">Pick up to 5 that best describe the event.</p>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="event-cats">
              {EVENT_CATEGORIES.map((c) => {
                const active = cats.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCats((p) => active ? p.filter((x) => x !== c) : p.length >= 5 ? p : [...p, c])}
                    className={CHIP_CLS(active)}
                    data-testid={`event-cat-${c}`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-6">
            <Label>Relevant industries</Label>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="event-industries">
              {INDUSTRIES.map((c) => {
                const active = industries.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setIndustries((p) => active ? p.filter((x) => x !== c) : p.length >= 5 ? p : [...p, c])}
                    className={CHIP_CLS(active)}
                    data-testid={`event-ind-${c}`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-6">
            <Label>Cover image</Label>
            <p className="text-xs text-[color:var(--ink-soft)]">Optional. Landscape works best. JPG/PNG under 3MB.</p>
            <div className="mt-3 flex items-center gap-4">
              <label
                htmlFor="event-image"
                className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-[color:var(--hairline)] bg-[color:var(--surface)] px-4 py-2 text-sm font-semibold text-[color:var(--ink)] hover:border-[color:var(--ink)]"
              >
                <ImageIcon className="h-4 w-4" /> {f.image ? "Change image" : "Upload image"}
              </label>
              <input id="event-image" type="file" accept="image/*" onChange={uploadImage} className="hidden" data-testid="event-image-input" />
              {f.image && (
                <div className="relative">
                  <img src={f.image} alt="preview" className="h-16 w-28 rounded-lg object-cover" />
                  <button type="button" onClick={() => set("image", null)} className="absolute -right-2 -top-2 rounded-full bg-[color:var(--invert-bg)] p-1 text-white" data-testid="event-image-remove">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="mt-8 flex items-start gap-3 rounded-2xl border border-[color:var(--hairline)] bg-[color:var(--soft)] p-4">
            <Checkbox
              id="prof-decl"
              checked={f.professional_declaration}
              onCheckedChange={(v) => set("professional_declaration", !!v)}
              data-testid="event-declaration"
            />
            <Label htmlFor="prof-decl" className="text-sm leading-snug text-[color:var(--ink)]">
              I confirm this is a professional or business event and follows Hub community guidelines.
              I understand it will be reviewed before it appears in the directory.
            </Label>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-end gap-3">
            <Link to="/app/events" className="rounded-full border border-[color:var(--hairline)] bg-[color:var(--surface)] px-5 py-2.5 text-sm font-semibold text-[color:var(--ink)] hover:border-[color:var(--ink)]">
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving}
              data-testid="event-submit"
              className="inline-flex items-center gap-2 rounded-full bg-[color:var(--invert-bg)] px-6 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {saving ? "Submitting…" : "Submit for review"}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <Label className="text-sm text-[color:var(--ink)]">{label}{required && <span className="text-[color:var(--brand-red)]"> *</span>}</Label>
        {hint && <span className="text-[11px] text-[color:var(--ink-soft)]">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
